import React from "react";
import { View, Text, Image, ImageBackground, StyleSheet } from "react-native";

import {
  CreditLineCard as CreditLineCardImg,
  DehaatHindi,
} from "app/farmer360/assets/images/index";
import { COLORS, PARAGRAPH_STYLES } from "@dehaat/dds";

interface Props {
  farmerName?: string;
  pendingEmi: number | string;
  dueDate: string;
}

const CreditHeroCard = ({ farmerName = "", pendingEmi, dueDate }: Props) => {
  const isPendingEmi = pendingEmi !== undefined && Number(pendingEmi) > 0;

  const badgeBg = isPendingEmi ? "#8C6500" : "#43A314";
  const badgeText = isPendingEmi && dueDate ? `Due On: ${dueDate}` : "No Due";
  const cardBorderColor = "#8C6500";

  return (
    <View style={styles.wrapper}>
      <ImageBackground
        source={CreditLineCardImg}
        resizeMode="cover"
        style={styles.imageBackground}
      >
        {/* LEFT: DeHaat logo + title */}
        <View style={styles.leftSection}>
          <Image
            source={DehaatHindi}
            style={styles.dehaatLogo}
            resizeMode="contain"
          />
          <View style={styles.titleBlock}>
            <Text style={styles.kisanText}>किसान</Text>
            <Text style={styles.krishiText}>कृषि कार्ड</Text>
          </View>
        </View>

        {/* RIGHT: badge + card panel */}
        <View style={styles.rightSection}>
          {/* Status badge */}
          <View style={[styles.badge, { backgroundColor: badgeBg }]}>
            <Text style={styles.badgeText}>{badgeText}</Text>
          </View>

          {/* White card panel */}
          {isPendingEmi ? (
            <View style={[styles.cardPanel, { borderColor: cardBorderColor }]}>
              <Text style={styles.pendingLabel}>Pending EMI</Text>
              <Text style={styles.emiAmount}>
                ₹{Number(pendingEmi).toLocaleString("en-IN")}
              </Text>
              <Text style={styles.dueDate}>{"Due Date: " + dueDate}</Text>
            </View>
          ) : null}
        </View>

        {/* Bottom name overlay */}
        <View style={styles.nameContainer}>
          <Text style={styles.nameText}>
            {farmerName?.toLocaleUpperCase("en-IN")}
          </Text>
        </View>
      </ImageBackground>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    width: "100%",
    maxWidth: 410,
    borderRadius: 20,
    overflow: "hidden",
    minHeight: 220,
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 5,
  },
  imageBackground: {
    flex: 1,
    position: "relative",
    paddingHorizontal: 20,
    paddingVertical: 8,
    paddingBottom: 36,
    minHeight: 220,
    borderRadius: 20,
    overflow: "hidden",
  },
  leftSection: {
    width: "52%",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    gap: 12,
    paddingTop: 2,
  },
  dehaatLogo: {
    width: 40,
    height: 30,
  },
  titleBlock: {
    flexDirection: "column",
    alignItems: "flex-start",
  },
  kisanText: {
    fontSize: 24,
    fontWeight: "700",
    color: "#1B5E20",
    lineHeight: 30,
  },
  dueDate: {
    ...PARAGRAPH_STYLES.MediumT3,
    color: COLORS.neutral70,
  },
  krishiText: {
    fontSize: 24,
    fontWeight: "700",
    color: "#1565C0",
    lineHeight: 30,
  },
  rightSection: {
    position: "absolute",
    right: 18,
    top: 42,
    width: 200,
    alignItems: "flex-start",
    justifyContent: "flex-end",
  },
  badge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    alignSelf: "flex-start",
  },
  badgeText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  cardPanel: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1.5,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    width: 200,
    minHeight: 100,
    alignItems: "stretch",
  },
  pendingLabel: {
    fontSize: 12,
    fontWeight: "500",
    color: "#555",
  },
  emiAmount: {
    fontSize: 18,
    fontWeight: "700",
    color: "#111",
    marginBottom: 10,
  },
  placeholderBar: {
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#CBD2D9",
    backgroundColor: "#ECEFF3",
  },
  nameContainer: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: "rgba(255,255,255,0.6)",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
    minHeight: 30,
    paddingHorizontal: 20,
    paddingVertical: 4,
    justifyContent: "center",
  },
  nameText: {
    fontSize: 13,
    fontWeight: "500",
    color: "#222",
  },
});

export default CreditHeroCard;
